import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BellRing,
  Briefcase,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  ClipboardList,
  Gavel,
  Inbox,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  Mic,
  Moon,
  PieChart,
  Plus,
  Scale,
  Settings as SettingsIcon,
  StickyNote,
  Sun,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import EventDialog from "@/components/EventDialog";
import { NotificationBell } from "@/components/NotificationBell";
import { OfflineIndicator } from "@/components/OfflineIndicator";
import { InstallPrompt } from "@/components/InstallPrompt";
import { DictateAnywhereDialog } from "@/components/DictateAnywhereDialog";
import { CommandPalette, SearchTrigger } from "@/components/CommandPalette";
import { backgroundUrl } from "@/components/BackgroundPicture";

const TEXTURES = {
  paper: "https://images.unsplash.com/photo-1616410731303-6affae095a0a?crop=entropy&cs=srgb&fm=jpg&q=70&w=1800",
  architecture:
    "https://images.unsplash.com/photo-1569944884489-aa5279c274c4?crop=entropy&cs=srgb&fm=jpg&q=70&w=1800",
};

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, testId: "nav-calendar" },
  { to: "/overview", label: "Overview", icon: PieChart, testId: "nav-overview" },
  { to: "/causelist", label: "Cause List", icon: ClipboardList, testId: "nav-causelist" },
  { to: "/brief", label: "Associate Brief", icon: Mic, testId: "nav-brief" },
  { to: "/todos", label: "To-Do", icon: ListChecks, testId: "nav-todos" },
  { to: "/due", label: "Due Board", icon: CalendarClock, testId: "nav-due" },
  { to: "/postits", label: "Post-Its", icon: StickyNote, testId: "nav-postits" },
  { to: "/uncategorized", label: "Uncategorized", icon: Inbox, testId: "nav-uncategorized" },
  { to: "/alerts", label: "Alerts", icon: BellRing, testId: "nav-alerts" },
  { to: "/matters", label: "Matters", icon: Briefcase, testId: "nav-matters" },
  { to: "/courts", label: "Courts", icon: Gavel, testId: "nav-courts" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

const CLERK_NAV = [
  { to: "/clerk", label: "Clerk Sheet", icon: ClipboardList, testId: "nav-clerk" },
];

const ROLE_LABEL = {
  owner: "Owner",
  limited_edit: "Limited edit",
  read_only: "Read only",
  clerk: "Clerk",
};

export default function AppShell() {
  const { user, workspace, workspaces, switchWorkspace, logout, savePreferences, canWrite, role } =
    useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [dictateOpen, setDictateOpen] = useState(false);
  const headerRef = useRef(null);
  const prefs = user?.preferences || {};
  const isClerk = role === "clerk";
  const navItems = isClerk ? CLERK_NAV : NAV;

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", prefs.theme === "dark");
  }, [prefs.theme]);

  const photo = useMemo(() => backgroundUrl(prefs), [prefs.background_path, prefs.background_version]);
  const texture = useMemo(() => (photo ? null : TEXTURES[prefs.texture]), [prefs.texture, photo]);

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--bg-strength",
      String(prefs.texture_opacity ?? 0.1),
    );
  }, [prefs.texture_opacity]);

  // page toolbars stick just below the top bar, whatever height it wraps to
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const measure = () =>
      document.documentElement.style.setProperty("--topbar-h", `${el.offsetHeight}px`);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const toggleTheme = () =>
    savePreferences({ ...prefs, theme: prefs.theme === "dark" ? "light" : "dark" });

  if (isClerk && !location.pathname.startsWith("/clerk")) {
    return <Navigate to="/clerk" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-clip">
      {photo ? (
        <div
          className="photo-layer"
          data-testid="background-photo-layer"
          style={{ backgroundImage: `url(${photo})` }}
        />
      ) : null}
      {texture ? (
        <div
          className="texture-layer"
          data-testid="texture-layer"
          style={{ backgroundImage: `url(${texture})` }}
        />
      ) : null}
      {!photo && prefs.texture === "grid" ? (
        <div
          className="texture-layer"
          data-testid="texture-layer"
          style={{
            backgroundImage:
              "linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      ) : null}

      <div className="relative z-10 flex min-h-screen">
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card">
          <Link
            to="/dashboard"
            data-testid="brand-home-link"
            className="flex items-center gap-3 px-5 py-5 border-b border-border"
          >
            <img
              src="/chambers-logo.png"
              alt="Chambers of Amir Arsiwala"
              className="h-14 w-14 shrink-0 object-contain"
            />
            <span className="font-heading text-xl font-semibold leading-tight tracking-tight">
              Chambers of
              <br />
              Amir Arsiwala
            </span>
          </Link>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(({ to, label, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testId}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-sm text-base font-medium transition-colors duration-200 ${
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent"
                  }`
                }
              >
                <Icon className="h-5 w-5" />
                {label}
              </NavLink>
            ))}
          </nav>
          {canWrite && !isClerk ? (
            <div className="p-4 border-t border-border">
              <Button
                data-testid="sidebar-new-entry-btn"
                onClick={() => setAddOpen(true)}
                className="w-full rounded-full h-12 text-base"
              >
                <Plus className="h-5 w-5 mr-2" /> New entry
              </Button>
            </div>
          ) : null}
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <header
            ref={headerRef}
            className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur-md"
          >
            <div className="px-4 sm:px-6 py-3 flex flex-wrap items-center gap-2 sm:gap-3 min-w-0">
              <button
                data-testid="mobile-menu-btn"
                className="lg:hidden h-10 w-10 grid place-items-center rounded-sm hover:bg-accent transition-colors duration-200"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <Menu className="h-5 w-5" />
              </button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="workspace-switcher"
                    className={`items-center gap-2 px-3 py-2 rounded-sm border border-border hover:bg-accent transition-colors duration-200 min-w-0 max-w-[38vw] md:max-w-[14rem] ${
                      workspaces.length > 1 ? "flex" : "hidden sm:flex"
                    }`}
                  >
                    <span className="truncate text-sm font-semibold">
                      {workspace?.name || "Diary"}
                    </span>
                    <span className="label-caps hidden lg:inline">
                      {ROLE_LABEL[workspace?.role] || ""}
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-60 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-72">
                  <DropdownMenuLabel className="label-caps">Diaries</DropdownMenuLabel>
                  {workspaces.map((ws) => (
                    <DropdownMenuItem
                      key={ws.workspace_id}
                      data-testid={`workspace-option-${ws.workspace_id}`}
                      onClick={() => {
                        switchWorkspace(ws);
                        navigate("/dashboard");
                      }}
                      className="flex flex-col items-start gap-0.5 py-2"
                    >
                      <span className="font-medium">{ws.name}</span>
                      <span className="text-xs text-muted-foreground">
                        {ws.owner_email} · {ROLE_LABEL[ws.role]}
                      </span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <div className="ml-auto flex items-center gap-2 shrink-0">
                <SearchTrigger onClick={() => setSearchOpen(true)} />
                {canWrite && !isClerk ? (
                  <button
                    data-testid="header-dictate-btn"
                    onClick={() => setDictateOpen(true)}
                    title="Dictate a note against any hearing"
                    className="h-10 px-3 inline-flex items-center gap-2 rounded-full border border-border hover:bg-accent transition-colors duration-200"
                  >
                    <Mic className="h-5 w-5" />
                    <span className="hidden xl:inline text-sm font-semibold">Dictate</span>
                  </button>
                ) : null}
                {canWrite ? (
                  <Button
                    data-testid="header-new-entry-btn"
                    onClick={() => setAddOpen(true)}
                    className="rounded-full h-10 px-4 lg:hidden"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                ) : null}
                <NotificationBell />
                <OfflineIndicator />
                <button
                  data-testid="theme-toggle"
                  onClick={toggleTheme}
                  className="h-10 w-10 grid place-items-center rounded-sm border border-border hover:bg-accent transition-colors duration-200"
                >
                  {prefs.theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                </button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      data-testid="user-menu"
                      className="flex items-center gap-2 pl-1 pr-3 py-1.5 rounded-full border border-border hover:bg-accent transition-colors duration-200"
                    >
                      {user?.picture ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          className="h-8 w-8 rounded-full object-cover"
                        />
                      ) : (
                        <span className="h-8 w-8 rounded-full bg-primary text-primary-foreground grid place-items-center text-sm font-semibold">
                          {user?.name?.[0]?.toUpperCase()}
                        </span>
                      )}
                      <span className="hidden xl:inline text-sm font-medium max-w-[10rem] truncate">
                        {user?.name}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    <DropdownMenuLabel>
                      <div className="text-sm font-semibold">{user?.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem data-testid="logout-btn" onClick={logout}>
                      <LogOut className="h-4 w-4 mr-2" /> Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {menuOpen ? (
              <nav className="lg:hidden border-t border-border p-3 grid grid-cols-2 gap-2 bg-card">
                {NAV.map(({ to, label, icon: Icon, testId }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`m-${testId}`}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-3 rounded-sm text-sm font-medium transition-colors duration-200 ${
                        isActive ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
              </nav>
            ) : null}
          </header>

          <main className="flex-1 px-4 sm:px-6 md:px-16 lg:px-20 py-8 lg:py-12">
            <Outlet />
          </main>
        </div>
      </div>

      <EventDialog open={addOpen} onOpenChange={setAddOpen} onSaved={() => window.dispatchEvent(new Event("diary-refresh"))} />
      <CommandPalette open={searchOpen} onOpenChange={setSearchOpen} />
      <DictateAnywhereDialog
        open={dictateOpen}
        onOpenChange={setDictateOpen}
        onSaved={() => window.dispatchEvent(new Event("diary-refresh"))}
      />
      <InstallPrompt />
    </div>
  );
}
